import { getCoordinateFromEvent } from './getCoordinateFromEvent.js';
import { isTouchEvent } from './isTouchEvent.js';

const DRAG_TAP_HOLD_TIMEOUT = 350;
const DRAG_THRESHOLD_IN_PX = 3;
const handleDrag = ({
  evt,
  sheet,
  onDrag,
  onDrop,
  onCancel
}) => {
  if (evt.type === "keydown") {
    return handleKeyboardDrag({
      evt,
      sheet,
      onDrag,
      onDrop,
      onCancel
    });
  }
  evt.stopPropagation();
  if (sheet === null) {
    return;
  }
  if ("button" in evt && evt.button !== 0) {
    // don't begin dragging on anything but a main button (left) click
    return;
  }
  const sheetBox = sheet.getBoundingClientRect();
  const firstCoordinate = getCoordinateFromEvent(evt);

  // dragging is not allowed on touch-based items until after a defined
  // threshold
  let canDrag = true;
  let canDragTimeout;
  if (isTouchEvent(evt)) {
    canDrag = false;
    canDragTimeout = window.setTimeout(() => {
      canDrag = true;
      onDrag({
        x: 0,
        y: 0
      }, true);
    }, DRAG_TAP_HOLD_TIMEOUT);
  }

  // keep track of whether the user has moved the cursor beyond the defined
  // threshold (at which point we begin handling drag)
  let isDragging = false;

  // emit updates using an animation frame loop to avoid overloading the CPU
  // with more actions than it can reasonably handle
  let animationFrameId;
  let nextEmission;
  let lastEmission;
  const handleAnimationFrame = () => {
    if (nextEmission !== undefined) {
      lastEmission = nextEmission;
      onDrag(nextEmission);
    }
    nextEmission = undefined;
    animationFrameId = requestAnimationFrame(handleAnimationFrame);
  };
  animationFrameId = requestAnimationFrame(handleAnimationFrame);

  // prevent onclick from happening after drag'n'drop session (this could
  // potentially mess with focus)
  const preventClick = evt => {
    evt.stopImmediatePropagation();
    window.removeEventListener("click", preventClick, {
      capture: true
    });
  };
  const handleDrag = evt => {
    const coord = getCoordinateFromEvent(evt);
    if (!canDrag) {
      if (Math.abs(coord.x - firstCoordinate.x) > DRAG_THRESHOLD_IN_PX || Math.abs(coord.y - firstCoordinate.y) > DRAG_THRESHOLD_IN_PX) {
        clearTimeout(canDragTimeout);
      }
      return;
    }
    const offset = {
      x: (coord.x - firstCoordinate.x) / sheetBox.width,
      y: (coord.y - firstCoordinate.y) / sheetBox.height
    };
    evt.preventDefault();
    evt.stopPropagation();
    if (!isDragging) {
      if (Math.abs(coord.x - firstCoordinate.x) > DRAG_THRESHOLD_IN_PX || Math.abs(coord.y - firstCoordinate.y) > DRAG_THRESHOLD_IN_PX) {
        isDragging = true;
      } else {
        return;
      }
    }
    nextEmission = offset;
  };
  const cleanupEvents = () => {
    window.removeEventListener("mousemove", handleDrag);
    window.removeEventListener("mouseup", handleDrop);
    window.removeEventListener("touchmove", handleDrag);
    window.removeEventListener("touchend", handleDrop);
    window.removeEventListener("touchcancel", handleDrop);
    setTimeout(() => {
      window.removeEventListener("click", preventClick, {
        capture: true
      });
      window.removeEventListener("keydown", handleEscape, {
        capture: true
      });
    });
  };
  const handleDrop = evt => {
    evt.preventDefault();
    evt.stopPropagation();
    isDragging = false;
    cleanupEvents();
    cancelAnimationFrame(animationFrameId);
    if (nextEmission !== undefined) {
      onDrag(nextEmission);
    }
    onDrop(nextEmission ?? lastEmission);
  };
  const handleEscape = evt => {
    if (evt.key !== "Escape") {
      return;
    }
    onCancel();
    cancelAnimationFrame(animationFrameId);
    cleanupEvents();
    evt.preventDefault();
    evt.stopPropagation();
  };
  if (isTouchEvent(evt)) {
    window.addEventListener("touchmove", handleDrag, {
      passive: false
    });
    window.addEventListener("touchend", handleDrop, {
      passive: false
    });
    window.addEventListener("touchcancel", handleDrop, {
      passive: false
    });
  } else {
    window.addEventListener("mousemove", handleDrag, {
      passive: false
    });
    window.addEventListener("mouseup", handleDrop, {
      passive: false
    });
  }
  window.addEventListener("click", preventClick, {
    capture: true
  });
  window.addEventListener("keydown", handleEscape, {
    capture: true
  });
  if (!isTouchEvent(evt)) {
    // allow scrolling even tough user is touching a moveable item; we do not
    // begin preventing scroll until after tap+hold timeout has been met
    evt.preventDefault();
  }
};
function handleKeyboardDrag({
  evt,
  sheet,
  onDrag,
  onDrop,
  onCancel
}) {
  evt.stopPropagation();
  const target = evt.currentTarget.closest("[data-drag-target]");
  if (!target || sheet === null) {
    return;
  }
  const sheetBox = sheet.getBoundingClientRect();
  const initalPosition = (() => {
    const rect = target.getBoundingClientRect();
    return {
      left: (rect.left - sheetBox.left) / sheetBox.width,
      right: (rect.right - sheetBox.left) / sheetBox.width,
      top: (rect.top - sheetBox.top) / sheetBox.height,
      bottom: (rect.bottom - sheetBox.top) / sheetBox.height
    };
  })();
  const offset = {
    x: 0,
    y: 0
  };

  // emit updates using an animation frame loop to avoid overloading the CPU
  // with more actions than it can reasonably handle
  let animationFrameId;
  let nextEmission;
  let lastEmission;
  const handleAnimationFrame = () => {
    if (nextEmission !== undefined) {
      lastEmission = nextEmission;
      onDrag(nextEmission);
    }
    nextEmission = undefined;
    animationFrameId = requestAnimationFrame(handleAnimationFrame);
  };
  animationFrameId = requestAnimationFrame(handleAnimationFrame);
  const handleKeyboardDrag = evt => {
    let didUpdate = false;
    switch (evt.key) {
      case " ":
      case "Enter":
        evt.preventDefault();
        handleDrop(evt);
        break;
      case "ArrowLeft":
        evt.preventDefault();
        offset.x -= 0.03;
        didUpdate = true;
        break;
      case "ArrowRight":
        evt.preventDefault();
        offset.x += 0.03;
        didUpdate = true;
        break;
      case "ArrowUp":
        evt.preventDefault();
        offset.y -= 0.03;
        didUpdate = true;
        break;
      case "ArrowDown":
        evt.preventDefault();
        offset.y += 0.03;
        didUpdate = true;
        break;
    }
    if (didUpdate) {
      offset.x = Math.max(initalPosition.left * -1, Math.min(1 - initalPosition.right, offset.x));
      offset.y = Math.max(initalPosition.top * -1, Math.min(1 - initalPosition.bottom, offset.y));
      handleDrag(offset);
    }
  };
  const handleDrag = offset => {
    evt.preventDefault();
    evt.stopPropagation();
    nextEmission = offset;
  };
  const cleanupEvents = () => {
    window.removeEventListener("keydown", handleKeyboardDrag);
    setTimeout(() => {
      window.removeEventListener("keydown", handleEscape, {
        capture: true
      });
    });
  };
  const handleDrop = evt => {
    evt.preventDefault();
    evt.stopPropagation();
    cleanupEvents();
    cancelAnimationFrame(animationFrameId);
    if (nextEmission !== undefined) {
      onDrag(nextEmission);
    }
    onDrop(nextEmission ?? lastEmission);
  };
  const handleEscape = evt => {
    if (evt.key !== "Escape") {
      return;
    }
    onCancel();
    cancelAnimationFrame(animationFrameId);
    cleanupEvents();
    evt.preventDefault();
    evt.stopPropagation();
  };
  window.addEventListener("keydown", handleKeyboardDrag, {
    passive: false
  });
  window.addEventListener("keydown", handleEscape, {
    capture: true
  });
  handleDrag(offset);
}

export { handleDrag };
//# sourceMappingURL=handleDrag.js.map
